import React, { createContext, useContext, ReactNode } from 'react';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { format, startOfDay, endOfDay } from 'date-fns';

const JST_TIMEZONE = 'Asia/Tokyo';

interface JSTContextValue {
  timezone: string;
  getJSTToday: () => Date;
  getJSTNow: () => Date;
  toJST: (date: Date) => Date;
  fromJST: (date: Date) => Date;
  formatJST: (date: Date, formatString: string) => string;
  getJSTStartOfDay: (date?: Date) => Date;
  getJSTEndOfDay: (date?: Date) => Date;
}

const JSTContext = createContext<JSTContextValue | undefined>(undefined);

interface JSTProviderProps {
  children: ReactNode;
}

export const JSTProvider: React.FC<JSTProviderProps> = ({ children }) => {
  const getJSTToday = (): Date => {
    const nowInJST = utcToZonedTime(new Date(), JST_TIMEZONE);
    return startOfDay(nowInJST);
  };

  const getJSTNow = (): Date => utcToZonedTime(new Date(), JST_TIMEZONE);

  const toJST = (date: Date): Date => utcToZonedTime(date, JST_TIMEZONE);

  const fromJST = (date: Date): Date => zonedTimeToUtc(date, JST_TIMEZONE);

  const formatJST = (date: Date, formatString: string): string => {
    const jstDate = utcToZonedTime(date, JST_TIMEZONE);
    return format(jstDate, formatString);
  };

  const getJSTStartOfDay = (date?: Date): Date => {
    const targetDate = date || new Date();
    const jstDate = utcToZonedTime(targetDate, JST_TIMEZONE);
    return startOfDay(jstDate);
  };

  const getJSTEndOfDay = (date?: Date): Date => {
    const targetDate = date || new Date();
    const jstDate = utcToZonedTime(targetDate, JST_TIMEZONE);
    return endOfDay(jstDate);
  };

  const value: JSTContextValue = {
    timezone: JST_TIMEZONE,
    getJSTToday,
    getJSTNow,
    toJST,
    fromJST,
    formatJST,
    getJSTStartOfDay,
    getJSTEndOfDay,
  };

  return <JSTContext.Provider value={value}>{children}</JSTContext.Provider>;
};

export const useJST = (): JSTContextValue => {
  const context = useContext(JSTContext);
  if (context === undefined) {
    throw new Error('useJST must be used within a JSTProvider');
  }
  return context;
};
